
  import Vue from "vue"
  import axios, { AxiosError, AxiosResponse } from "axios"
  import { EventBus } from "@/plugins/eventBus"
  
  interface Permission {
    name: string,
    value: string
  }
  
  interface Space {
    id: number,
    name: string
  }

  export default Vue.extend({
    name: 'UserFormUpdate',
    data: () => ({
      tab: null,
      route: "admin",
      id: null as string|null,
      formUser: {
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
        password: "",
        selected_spaces: [] as number[],
        selected_permissions: [],
        administrator: false,
      },
      searchedSpaces: [] as {id: number, name: string}[],
      show: false,
      fetchedUser: {
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
        password: "",
        spaces: [] as Array<Space>,
        permissions: [],
        administrator: false,
        apiAccess: false
      },
      search: null,
      disabled: true,
      emailRegex: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[\d]{1,3}\.[\d]{1,3}\.[\d]{1,3}\.[\d]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    }),
    computed: {    
      emailRule() {
        return (v: string) => (this.emailRegex.test(v) || !v) || this.$vuetify.lang.t('$vuetify.invalid_email')
      },
      requiredRule() {
        return (v: string) => !!v || this.$vuetify.lang.t('$vuetify.required_field')
      },
      nameLengthRule() {
        return (v: string) => ((v.length > 1 && v.length < 100) || !v) || this.$vuetify.lang.t('$vuetify.invalid_length')
      },
      stringLengthRule() {
        return (v: string) => ((v.length > 1 && v.length < 254) || !v) || this.$vuetify.lang.t('$vuetify.invalid_length')
      },
      phoneNumberRule() {
        return (v: string) => (!v || (5 <= v.length && v.length <= 20)) || this.$vuetify.lang.t('$vuetify.invalid_length')
      },
      atLeastOneRequired() {
        return (v: Array<number>) => ((v.length > 0) || !v) || this.$vuetify.lang.t('$vuetify.required_field')
      },
      availablePermissions() {
        const permissionNames = Object.keys(this.$vuetify.lang.locales.en.permissions_names).filter((permission) => {
          return permission
        })
        let permissions: Array<Permission> = []
  
        permissionNames.forEach(permission => {
          permissions.push({"name": this.$vuetify.lang.t('$vuetify.permissions_names.' + permission), "value": permission})
        })

        return permissions
      },
      spaces() : {id: number, name: string}[] {
        // We remove duplicates between what has been fetch on initialization and research
        let uniqSpaceIds = [] as number[]
        let concatSpaces = this.fetchedUser.spaces.concat(this.searchedSpaces) as {id: number, name: string}[]
        const uniqueSpaces = concatSpaces.filter((element: {id: number, name: string}) => {
          const isDuplicate = uniqSpaceIds.includes(element.id)
          if (!isDuplicate) {
            uniqSpaceIds.push(element.id)
            return true
          }
          return false
        })
        return uniqueSpaces
      }
    },
    mounted() {
      this.getSpaces()
      this.getUser(Number(this.$route.params.id))
    },
    watch: {
      search(val) {
        this.getSpaces(val)
      },
      $route () {
        this.getUser(Number(this.$route.params.id))
      }
    },
    methods: {
      updateUser() {
        if ((this.$refs.form as Vue & { validate: () => boolean }).validate()) {
          let body: {[k: string]: string|string[]|boolean} = {}

          // compare the form data against the fetched data
          if (this.formUser.firstname !== this.fetchedUser.firstname) {
            body.firstname = this.formUser.firstname
          }
          if (this.formUser.lastname !== this.fetchedUser.lastname) {
            body.lastname = this.formUser.lastname
          }
          if (this.formUser.email !== this.fetchedUser.email) {
            body.email = this.formUser.email
          }
          if (this.formUser.phone !== this.fetchedUser.phone) {
            body.phone = this.formUser.phone
          }
          if (this.formUser.password !== "") {
            body.password = this.formUser.password
          }
          if (this.formUser.selected_permissions !== this.fetchedUser.permissions) {
            body.api_roles = this.formUser.selected_permissions
          }
          if (this.formUser.administrator !== this.fetchedUser.administrator) {
            body.administrator = this.formUser.administrator
          }

          // add the spaces found in the fetched data but missing in the form
          // We store the ids of the spaces in an array for easier add/remove qualification
          const fetchedSpacesIds = this.fetchedUser.spaces.map(space => space.id)
          
          // We keep spaces found in the form but not in fetched data
          const spacesToAdd = this.formUser.selected_spaces.filter(id => !fetchedSpacesIds.includes(id))
          
          // If spaces are found, we add them to the user
          if (spacesToAdd.length > 0) {
            Object.assign(body, {add_spaces: spacesToAdd})
          }

          // We keep spaces found in fetched data but not in the form
          const spacesToRemove = fetchedSpacesIds.filter(id => !this.formUser.selected_spaces.includes(id))
          // If spaces are found, we remove them from the user
          if (spacesToRemove.length > 0) {
            Object.assign(body, {remove_spaces: spacesToRemove})
          }

          axios
            .put(`/v1/admin/users/`, {
              [this.$route.params.id]: body
            })
            .then(() => {
              EventBus.$emit('listParagonDataReload')
              this.getUser(Number(this.$route.params.id))
              EventBus.$emit('snackbar',{message: this.$vuetify.lang.t('$vuetify.user_successfully_updated')})
            })
            .catch((error: AxiosError) => {
              EventBus.$emit('listParagonDataReload')
              EventBus.$emit('snackbar',{axiosError: error})
            }).finally(() => {
              this.disabled = true
            })
        }
      },
      getUser(id: number) {
        axios
          .get(`/v1/admin/users/${id}`)
          .then((response: AxiosResponse) => {
            // fill the form
            this.formUser.firstname = response.data['firstname']
            this.formUser.lastname = response.data['lastname']
            this.formUser.email = response.data['email']
            this.formUser.phone = response.data['phone']
            this.formUser.selected_spaces = response.data['spaces'].map((space:{ id: string, name: string}) => space.id)
            this.formUser.selected_permissions = response.data['permissions']
            this.formUser.administrator = response.data['administrator']

            // the user's data to keep track of modifications
            this.fetchedUser.firstname = response.data['firstname']
            this.fetchedUser.lastname = response.data['lastname']
            this.fetchedUser.email = response.data['email']
            this.fetchedUser.phone = response.data['phone']
            this.fetchedUser.spaces = response.data['spaces']
            this.fetchedUser.permissions = response.data['permissions']
            this.fetchedUser.administrator = response.data['administrator']
            this.fetchedUser.apiAccess = Boolean(response.data['api_access'])

          })
          .catch((error: AxiosError) => {
            EventBus.$emit('snackbar',{axiosError: error})
          })
          .finally(() =>{
            this.disabled = true
          })
      },
      getSpaces(search = null) {
        let url = "/v1/admin/spaces"
        url += search !== null ? `?page=1&name=${search}` : ""
        axios
          .get(url)
          .then((response: AxiosResponse) => {
            this.searchedSpaces = response.data
          })
          .catch((error: AxiosError) => {
            EventBus.$emit('snackbar',{axiosError: error})
          })
      }
    }
  })
